/**
 * Landing by itself (`autoLand`, §5.5, §5.16) — the steps a member's merge
 * dialog would otherwise wait on, each a switch offered only once the one
 * before it is on, as the app draws them. Five values in one setting, so the
 * row has no value column and this is the value, under its line.
 *
 * The clamp is the app's own (`@airlock/contracts/settings/auto-land`): what
 * leaves this field is exactly what a member's machine and the Worker keep.
 */
import type React from 'react';
import { autoLand } from '@airlock/contracts/settings/auto-land';
import { DEFAULT_SETTINGS } from '@airlock/contracts/settings/defaults';
import type { AutoLand } from '@airlock/contracts/settings/types';
import { color } from '../../theme/tokens';
import { SettingSelect, SettingToggle } from '../../components/settings-row';

type Step = 'branch' | 'message' | 'push' | 'pullRequest';
const STEPS: { key: Step; label: string }[] = [
  { key: 'branch', label: 'Name the branch' },
  { key: 'message', label: 'Write the commit and merge' },
  { key: 'push', label: 'Push' },
  { key: 'pullRequest', label: 'Open the pull request' }
];

const SESSION_OPTIONS = [
  { value: 'off', label: 'Stays open' },
  { value: 'stop', label: 'Stopped' },
  { value: 'close', label: 'Closed' }
];

/** The shown value, clamped whatever was stored: a partial answer reads as the steps it really runs. */
export function landOf(value: unknown): AutoLand {
  return autoLand(value, DEFAULT_SETTINGS.autoLand);
}

function Line({ label, held, children }: { label: string; held?: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '7px 0',
        boxShadow: `inset 0 -1px 0 ${color.hairSoft}`
      }}
    >
      <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: held ? color.inkFaint : color.inkBody }}>
        {label}
        {held && <span style={{ marginLeft: 8, fontSize: 12 }}>· turn on “{held}” first</span>}
      </span>
      {children}
    </div>
  );
}

export function AutoLandField({
  value,
  onChange,
  editable = true
}: {
  value: unknown;
  onChange: (next: AutoLand) => void;
  editable?: boolean;
}): React.JSX.Element {
  const land = landOf(value);
  // Through the clamp, so a step switched off takes the ones after it along.
  const put = (patch: Partial<AutoLand>): void => onChange(autoLand({ ...land, ...patch }, DEFAULT_SETTINGS.autoLand));
  return (
    <div>
      {STEPS.map((step, index) => {
        const previous = STEPS[index - 1];
        const open = !previous || land[previous.key];
        return (
          <Line key={step.key} label={step.label} held={open ? undefined : previous.label}>
            {editable && open ? (
              <SettingToggle on={land[step.key]} onToggle={() => put({ [step.key]: !land[step.key] })} />
            ) : (
              <span style={{ fontSize: 12.5, color: color.inkMuted }}>{land[step.key] ? 'By itself' : 'Off'}</span>
            )}
          </Line>
        );
      })}
      <Line label="Then the session" held={land.message ? undefined : 'Write the commit and merge'}>
        {editable && land.message ? (
          <SettingSelect
            value={land.session}
            options={SESSION_OPTIONS}
            onChange={(next) => put({ session: next as AutoLand['session'] })}
          />
        ) : (
          <span style={{ fontSize: 12.5, color: color.inkMuted }}>
            {SESSION_OPTIONS.find((option) => option.value === land.session)?.label}
          </span>
        )}
      </Line>
    </div>
  );
}
